const ScrollLock = (() => {
  function lock() {
    document.body.style.position = "fixed";
    document.body.style.top = `0`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
  }

  function unlock() {
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    document.body.style.overflow = "";
  }

  function cleanupIfFixed() {
    if (getComputedStyle(document.body).position === "fixed") {
      unlock();
    }
  }

  return { lock, unlock, cleanupIfFixed };
})();

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.scrollY = 0;
    this.loaderOverlay = null;

    this.setupEventListeners();
    // Ensure scroll is unlocked when the component initializes
    this.ensureScrollUnlocked();
    this._enterHandler = this._enterHandler?.bind(this);
  }

  connectedCallback() {
    super.connectedCallback?.();
    ["keydown", "keyup", "keypress"].forEach((t) => window.addEventListener(t, this._handleEnterKey, true));
  }

  disconnectedCallback() {
    ["keydown", "keyup", "keypress"].forEach((t) => window.removeEventListener(t, this._handleEnterKey, true));
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.handleViewportResize);
      window.visualViewport.removeEventListener("scroll", this.handleViewportResize);
    } else {
      window.removeEventListener("resize", this.handleViewportResize);
    }
    super.disconnectedCallback?.();
  }

  ensureScrollUnlocked() {
    ScrollLock.cleanupIfFixed();
  }

  createSearchLoader() {
    // Check if loader exists in DOM (it might have been removed when innerHTML was replaced)
    if (this.loaderOverlay && !document.body.contains(this.loaderOverlay)) {
      this.loaderOverlay = null;
    }
    
    if (!this.loaderOverlay) {
      const searchContainer = this.querySelector(".predictive-search.predictive-search--header");
      if (searchContainer) {
        this.loaderOverlay = document.createElement("div");
        this.loaderOverlay.className = "predictive-search-loader-overlay";
        this.loaderOverlay.innerHTML = `<span class="loader"></span>`;
        searchContainer.appendChild(this.loaderOverlay);
      }
    }
    return this.loaderOverlay;
  }

  showLoader() {
    const searchContainer = this.querySelector(".predictive-search.predictive-search--header");
    const loader = this.createSearchLoader();
    if (loader && searchContainer) {
      searchContainer.scrollTop = 0;
      searchContainer.classList.add("search-loading");
      loader.classList.add("show");
    }
  }

  hideLoader() {
    if (this.loaderOverlay && document.body.contains(this.loaderOverlay)) {
      this.loaderOverlay.classList.remove("show");
    }
    const searchContainer = this.querySelector(".predictive-search.predictive-search--header");
    if (searchContainer) {
      searchContainer.classList.remove("search-loading");
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.input.addEventListener("click", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    document.querySelector(".search-modal__close-button").addEventListener("click", this.onModalClose.bind(this));
    document.querySelector(".search-modal__clear-button").addEventListener("click", this.onFormReset.bind(this));
    // Remove lockScroll from search icon click - only lock scroll when results are actually displayed
    document.querySelector(".header__icon--search").addEventListener("click", this.onSearchIconClick.bind(this));
   
    this.handleViewportResize = this.handleViewportResize.bind(this);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this.handleViewportResize);
      window.visualViewport.addEventListener("scroll", this.handleViewportResize);
    } else {
      window.addEventListener("resize", this.handleViewportResize);
    }
  }

  onSearchIconClick() {
    // Open search bar without suggestions. Suggestions will be opened on input explicit focus.
    this.onFormReset();
  }

  getQuery() {
    return this.input.value.trim();
  }

  lockScroll() {
    ScrollLock.lock();
  }

  unlockScroll() {
    ScrollLock.unlock();
  }

  onModalClose() {
    const openDetailsElement = document.querySelectorAll("details[open]");
    openDetailsElement.forEach((element) => {
      const summaryElement = element.querySelector("summary");
      element.removeAttribute("open");
      summaryElement.setAttribute("aria-expanded", false);
      summaryElement.focus();
    });
    this.searchTerm = "";
    this.abortController.abort();
    this.abortController = new AbortController();
    this.hideLoader();
    this.onChange(true);
    document.querySelector(".search-modal__clear-button")?.classList.add("hide");
    document.querySelector(".no-results-search")?.classList.add("hide");
    this.querySelector(".predictive-search__result-group")?.remove();
    this.querySelector(".predictive-search__search-for-button")?.remove();
    document.body.classList.remove("overflow-hidden");
    this.unlockScroll();
    this.closeResults(true);
  }

  onChange() {
    super.onChange();
    if (window.innerWidth <= 750) {
      const predictiveSearchContainer = this.querySelector(".predictive-search");
      if (predictiveSearchContainer) {
        const mobileHeight = this.getMobileContainerHeight();
        predictiveSearchContainer.style.height = `${mobileHeight}px`;
        predictiveSearchContainer.style.maxHeight = `calc(100dvh - 120px)`;
      }
    } else {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    const newSearchTerm = this.getQuery();
    
    // Abort any pending requests when search term changes significantly
    if (!newSearchTerm.length || !this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.abortController.abort();
      this.abortController = new AbortController();
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector(".predictive-search__result-group")?.remove();
      this.querySelector(".predictive-search__search-for-button")?.remove();
      this.hideLoader();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // No search term, ensure scroll is unlocked and show default content
      this.hideLoader(); 
      this.unlockScroll();
      // Ensure any result groups are removed (in case async response came after abort)
      this.querySelector(".predictive-search__result-group")?.remove();
      this.querySelector(".predictive-search__search-for-button")?.remove();
      this.querySelector(".default-search-result")?.classList.remove("hide");
      document.querySelector(".search-modal__clear-button")?.classList.add("hide");
      document.querySelector(".no-results-search")?.classList.add("hide");
      this.removeAttribute("results");
      // Close any open results to prevent scroll locking
      this.closeResults(true);
      return;
    }

    document.querySelector(".search-modal__clear-button")?.classList.remove("hide");

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const isQuickAddButton = event?.submitter?.classList?.value?.includes("color-swatch-item");

    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a') || isQuickAddButton) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.hideLoader();
      // Unlock scroll first before calling onChange
      this.unlockScroll();
      this.onChange();
      document.querySelector(".search-modal__clear-button")?.classList.add("hide");
      document.querySelector(".no-results-search")?.classList.add("hide");
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      // Ensure any stale results are removed before showing default content
      this.querySelector(".predictive-search__result-group")?.remove();
      this.querySelector(".predictive-search__search-for-button")?.remove();
      this.removeAttribute("results");
      this.searchTerm = "";
      this.open();
      this.querySelector(".default-search-result")?.classList.remove("hide");
       if (window.innerWidth <= 750) {
        setTimeout(() => this.handleViewportResize(), 300);
      }
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
      if (window.innerWidth <= 750) {
        setTimeout(() => this.handleViewportResize(), 300);
      }
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      const activeElement = document.activeElement;
      const isCartDrawer = activeElement?.closest("cart-drawer") || activeElement?.closest("#CartDrawer");
      const cartDrawer = document.querySelector("cart-drawer");
      const isCartDrawerOpen = cartDrawer?.classList.contains("active") || cartDrawer?.isOpen;
      const hasSearchTerm = this.searchTerm && this.searchTerm.length > 0;
      const detailsElement = this.closest("details");
      const isDetailsOpen = detailsElement?.hasAttribute("open");
     
      if (!this.contains(activeElement) && !isCartDrawer && !isCartDrawerOpen && !hasSearchTerm && !isDetailsOpen) {
        this.close();
      }
    });
  }

  _handleEnterKey = (e) => {
    if (!this.isOpen) return;

    const within = this.contains(e.target) || e.target.closest("predictive-search") === this;
    if (!within || e.isComposing) return;

    const isEnter = e.key === "Enter" || e.code === "Enter" || e.code === "NumpadEnter" || e.keyCode === 13;
    if (!isEnter) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    const q = (this.getQuery?.() || this.input?.value || "").trim();
    if (!q) return;

    const form = this.input?.form;
    if (typeof form?.requestSubmit === "function") form.requestSubmit();
    else form?.submit?.();
  };

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g"))?.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.showLoader();

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.hideLoader();
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        // Skip rendering if the user has already typed a different term
        if (searchTerm !== this.searchTerm) {
          this.hideLoader();
          return;
        }
        this.renderSearchResults(resultsMarkup);
        this.hideLoader();
      })
      .catch((error) => {
        this.hideLoader();
        if (error?.code === 20) {
          this.removeAttribute("loading");
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    // Don't render results if search term has been cleared
    if (!this.searchTerm || !this.searchTerm.length) {
      this.hideLoader();
      return;
    }
    
    // Pre-calculate and set height for mobile before inserting HTML to prevent flash
    if (window.innerWidth <= 750) {
      const predictiveSearchContainer = this.querySelector(".predictive-search");
      if (predictiveSearchContainer) {
        const mobileHeight = this.getMobileContainerHeight();
        predictiveSearchContainer.style.height = `${mobileHeight}px`;
        predictiveSearchContainer.style.maxHeight = `calc(100dvh - 120px)`;
      }
    }
   
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("open", true);
    this.isOpen = true;
    this.input.setAttribute("aria-expanded", true);
    this.removeAttribute("loading");
    this.setAttribute("results", true);
    this.dispatchEvent(
      new CustomEvent("predictive-search:results-updated", {
        bubbles: true,
        detail: {
          searchTerm: this.searchTerm,
        },
      }),
    );

    const hasRenderableResults =
      this.querySelector(".predictive-search__result-group") ||
      this.querySelector(".predictive-search__search-for-button");

    const isExplicitEmpty =
      this.querySelector(".no-results-search") || this.querySelector('[data-predictive-search-empty="true"]');
    if (hasRenderableResults) {
      document.querySelectorAll(".predictive-search .featured-card-product .product-form__submit").forEach((btn) => {
        btn.addEventListener(
          "click",
          () => {
            const currentSearchTerm = this.searchTerm;
            document.addEventListener(
              "cart:predictive-add",
              () => {
                if (currentSearchTerm && currentSearchTerm.length > 0) {
                  if (this.input.value !== currentSearchTerm) {
                    this.input.value = currentSearchTerm;
                    this.searchTerm = currentSearchTerm;
                  }
                  if (!this.isOpen) {
                    this.open();
                  }
                }
              },
              { once: true },
            );
          },
          { once: true },
        );
      });
      this.setLiveRegionResults();
      this.open();
    } else {
      const liveText =
        this.querySelector("[data-predictive-search-live-region-count-value]")?.textContent?.trim() || "";
      if (liveText) this.setLiveRegionText(liveText);
      this.unlockScroll();
      this.setAttribute("results", false);
      this.classList.toggle("predictive-search--empty", !!isExplicitEmpty);
    }
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const text = this.querySelector("[data-predictive-search-live-region-count-value]")?.textContent || "";
    this.setLiveRegionText(text);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector(".section-header")?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }
 
  getMobileContainerHeight() {
    // Get the actual available viewport height (accounts for keyboard)
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const searchInput = this.input;
    const inputRect = searchInput?.getBoundingClientRect();
    const inputBottom = inputRect?.bottom || 0;
   
    const availableHeight = viewportHeight - inputBottom - 20; // 20px padding
   
    const minHeight = 200;
   
    return Math.max(minHeight, availableHeight);
  }
 
  handleViewportResize() {
    if (window.innerWidth <= 750 && this.isOpen && this.getAttribute("open") === "true") {
      const predictiveSearchContainer = this.querySelector(".predictive-search");
      if (predictiveSearchContainer) {
        const newHeight = this.getMobileContainerHeight();
        const currentHeight = parseInt(predictiveSearchContainer.style.height) || newHeight;
        if (Math.abs(newHeight - currentHeight) > 10) {
          predictiveSearchContainer.style.height = `${newHeight}px`;
          predictiveSearchContainer.style.maxHeight = `calc(100dvh - 120px)`;
        }
      }
    }
  }

  shouldLockScroll() {
    // Only lock scroll if we have actual search results AND the search is open
    return (
      this.getAttribute("results") === "true" && this.getAttribute("open") === "true" && this.searchTerm.length > 0
    );
  }

  open() {
    this.setAttribute("open", true);
    this.lockScroll();

    if (window.innerWidth <= 750) {
      const predictiveSearchContainer = this.querySelector(".predictive-search");
      if (predictiveSearchContainer) {
        const mobileHeight = this.getMobileContainerHeight();
        predictiveSearchContainer.style.height = `${mobileHeight}px`;
        predictiveSearchContainer.style.maxHeight = `calc(100dvh - 120px)`;
        setTimeout(() => {
          const updatedHeight = this.getMobileContainerHeight();
          if (Math.abs(updatedHeight - mobileHeight) > 10) {
            predictiveSearchContainer.style.height = `${updatedHeight}px`;
          }
        }, 100);
      }
    } else {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
   
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;

    document.getElementById("stickyPanel")?.classList?.remove("visible");
    document.getElementsByClassName("sticky-atc-mobile")[0]?.classList?.remove("visible");
  }

  close(clearSearchTerm = false) {
    this.hideLoader();
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    const details = this.closest("details");

    if (!details?.open) {
      this.unlockScroll();
    }

    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
      // Ensure scroll is unlocked when clearing search
      this.unlockScroll();
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);
